import { FC, useState } from 'react'

import { F0TagRaw } from '@factorialco/f0-react'
import { Widget } from '@factorialco/f0-react/dist/experimental'
import {
  Briefcase as BriefcaseIcon,
  Home as HomeIcon,
  Office as OfficeIcon,
} from '@factorialco/f0-react/icons/app'
import { StatusInsightDocument, StatusInsightQuery } from 'generated/resources/graphql'

import { useUnsafeCallback as useCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect as useEffect } from 'modules/core/hooks/useEffect'
import { useMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'
import { buildWidget } from 'modules/core/lib/widgets'

import {
  MinutesFor,
  ShiftsData,
  formatClockInTime,
  getDailyShiftDisplayText,
  getShiftData,
} from './helpers'

const prepareData = (data: StatusInsightQuery) => {
  const attendanceShifts = data?.attendance.shiftsConnection.nodes || []

  const {
    clockInLocation,
    clockInLocationType,
    clockInProject,
    shiftStatus,
    dailyShift,
    hasCompletedDay,
  } = ShiftsData(attendanceShifts)

  return {
    attendanceShifts,
    clockInLocation,
    clockInLocationType,
    clockInProject,
    shiftStatus,
    dailyShift,
    hasCompletedDay,
  }
}

type PreparedData = ReturnType<typeof prepareData>

const StatusWidgetComponent: FC<{ data: PreparedData; employeeId: number }> = ({
  data: {
    attendanceShifts,
    clockInLocation,
    clockInLocationType,
    clockInProject,
    shiftStatus,
    dailyShift,
    hasCompletedDay,
  },
}) => {
  const [totalMinutes, setTotalMinutes] = useState(0)

  const calculateTotalMinutes = useCallback(() => {
    setTotalMinutes(MinutesFor(attendanceShifts))
  }, [attendanceShifts])

  useEffect(() => {
    if (!dailyShift) {
      calculateTotalMinutes()
      const intervalId = setInterval(calculateTotalMinutes, 60000)
      return () => clearInterval(intervalId)
    }
  }, [calculateTotalMinutes, dailyShift])

  const clockInLocationIcon = useMemo(() => {
    if (clockInLocationType === 'business_trip') {
      return BriefcaseIcon
    }

    if (clockInLocationType === 'office') {
      return OfficeIcon
    }

    if (clockInLocationType === 'work_from_home') {
      return HomeIcon
    }
  }, [clockInLocationType])

  const hours = formatClockInTime(Math.floor(totalMinutes / 60))
  const minutes = formatClockInTime(totalMinutes % 60)
  const displayTime = `${hours}:${minutes}`

  const { text: shiftStatusString, value: shiftStatusValue } = getShiftData(shiftStatus)

  return (
    <Widget
      header={{
        title: i18n.t('insights.widgets.status.title'),
      }}
      status={{
        text: shiftStatusString,
        variant: shiftStatusValue,
      }}
    >
      <div className="flex flex-col gap-3 items-start">
        <p className="text-3xl font-semibold">
          {dailyShift ? getDailyShiftDisplayText(dailyShift, hasCompletedDay) : displayTime}
        </p>
        {clockInLocation && clockInLocationIcon && (
          <F0TagRaw text={clockInLocation} icon={clockInLocationIcon} />
        )}
        {clockInProject && <F0TagRaw text={clockInProject} icon={BriefcaseIcon} />}
      </div>
    </Widget>
  )
}

export const buildStatusWidget = () =>
  buildWidget({
    id: 'status',
    document: StatusInsightDocument,
    prepareData,
    component: StatusWidgetComponent,
    policies: ['seeAttendance'],
  })
